'use client'

import type { AriaAttributes, KeyboardEvent as ReactKeyboardEvent } from 'react'
import type { CountryCode } from './countries'
import { ChevronDown } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { cn } from '../cn'
import { countryOptions, filterCountries } from './countries'

// Same field as AddressAutocomplete / the shadcn Input, with room for the chevron.
const INPUT_CLASS
  = 'h-9 w-full min-w-0 rounded-md border border-input bg-transparent py-1 pl-3 pr-9 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20'

interface Props extends Pick<AriaAttributes, 'aria-invalid' | 'aria-describedby'> {
  value: CountryCode | null
  onChange: (code: CountryCode) => void
  /** BCP 47 locale the country names are written in. */
  locale: string
  id?: string
  placeholder?: string
  noResultsText?: string
  disabled?: boolean
  className?: string
}

/**
 * Type-to-search country picker (ARIA 1.2 combobox). Every ISO 3166-1 country,
 * named in `locale`; the buyer can type a name in any accent/case or an ISO
 * code. Leaving the field without picking restores the current selection, so
 * the value is always a real country or null.
 */
export function CountryCombobox({
  value,
  onChange,
  locale,
  id,
  placeholder,
  noResultsText = 'No matches',
  disabled,
  className,
  ...aria
}: Props) {
  const listId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const options = useMemo(() => countryOptions(locale), [locale])
  const selectedName = options.find(o => o.code === value)?.name ?? ''

  const [query, setQuery] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const matches = useMemo(() => filterCountries(options, query ?? ''), [options, query])

  function close() {
    setOpen(false)
    setQuery(null)
    setActiveIndex(-1)
  }

  // Opening on the current selection scrolls it into view.
  function openList() {
    if (open)
      return
    setOpen(true)
    setActiveIndex(options.findIndex(o => o.code === value))
  }

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (containerRef.current?.contains(e.target as Node))
        return
      setOpen(false)
      setQuery(null)
      setActiveIndex(-1)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  // Escape closes the open list and nothing else. Inside a Radix dialog or
  // sheet, the dialog listens for Escape on the document in the capture phase
  // — before this input sees it — and would close itself (a payment sheet
  // would abandon its reservation). A window capture listener runs first, and
  // Radix skips dismissal for an event that is already default-prevented.
  useEffect(() => {
    if (!open)
      return
    function onEscape(e: KeyboardEvent) {
      if (e.key !== 'Escape' || document.activeElement !== inputRef.current)
        return
      e.preventDefault()
      setOpen(false)
      setQuery(null)
      setActiveIndex(-1)
    }
    window.addEventListener('keydown', onEscape, true)
    return () => window.removeEventListener('keydown', onEscape, true)
  }, [open])

  useEffect(() => {
    if (activeIndex >= 0)
      listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  function choose(code: CountryCode) {
    onChange(code)
    close()
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActiveIndex(i => (matches.length === 0 ? -1 : (i + 1) % matches.length))
    }
    else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setOpen(true)
      setActiveIndex(i => (i <= 0 ? matches.length - 1 : i - 1))
    }
    else if (e.key === 'Enter' && open) {
      e.preventDefault()
      const pick = matches[activeIndex >= 0 ? activeIndex : 0]
      if (pick)
        choose(pick.code)
    }
  }

  const optionId = (i: number) => `${listId}-option-${i}`

  return (
    <div ref={containerRef} className="relative">
      <input
        ref={inputRef}
        id={id}
        value={query ?? selectedName}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-invalid={aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        className={cn(INPUT_CLASS, className)}
        onFocus={e => e.currentTarget.select()}
        onClick={openList}
        onChange={(e) => {
          setQuery(e.target.value)
          setActiveIndex(0)
          setOpen(true)
        }}
        onBlur={(e) => {
          if (!containerRef.current?.contains(e.relatedTarget as Node | null))
            close()
        }}
        onKeyDown={onKeyDown}
      />
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border border-border bg-background p-1 text-foreground shadow-md"
        >
          {matches.length === 0
            ? <li className="px-3 py-2 text-sm text-muted-foreground">{noResultsText}</li>
            : matches.map((option, i) => (
                <li
                  key={option.code}
                  id={optionId(i)}
                  role="option"
                  aria-selected={option.code === value}
                  // Keep focus in the input so blur doesn't close the list first.
                  onMouseDown={e => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(option.code)}
                  className={cn(
                    'cursor-pointer truncate rounded-sm px-3 py-2 text-sm transition-colors',
                    i === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground',
                    option.code === value && 'font-medium',
                  )}
                >
                  {option.name}
                </li>
              ))}
        </ul>
      )}
    </div>
  )
}
